import { useCallback } from 'react';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { normalizeUrlForHref } from 'src/app/shared/components/endorsements';
import type { EndorsementFormData } from 'src/app/shared/components/endorsements';
import type { UploaderMethodProps } from 'src/app/shared/components/imageUpload';

import { createEndorsement } from '../../api/createEndorsement';
import { getErrorType, getPhotoUrl } from '../helpers';

import type { EndorsementError } from './useEndorsementForm';

type SubmitParams = {
	petitionId: string;
	formData: EndorsementFormData;
	utilityContext: UtilityContext;
	track: (event: string, data?: Record<string, unknown>) => Promise<boolean>;
	onSuccess: () => void;
	onError: (error: EndorsementError) => void;
	setIsSubmitting: (value: boolean) => void;
	uploader: UploaderMethodProps;
	image: string | undefined;
};

function buildEndorsementInput(petitionId: string, formData: EndorsementFormData, photoUrl?: string) {
	return {
		petitionId,
		name: formData.name,
		title: formData.title || undefined,
		url: formData.url ? normalizeUrlForHref(formData.url) : undefined,
		content: formData.statement || undefined,
		...(photoUrl && { photo: { url: photoUrl } }),
	};
}

function buildTrackingData(formData: EndorsementFormData) {
	return {
		person_org: formData.name,
		role: formData.title || '',
		why_support: formData.statement || '',
		photo: formData.photoUrl || '',
		url: formData.url ? normalizeUrlForHref(formData.url) : '',
	};
}

function handleSubmitError(
	track: SubmitParams['track'],
	onError: SubmitParams['onError'],
	msg: string,
	type: EndorsementError['type'],
) {
	void track('starter_dashboard_endorsement_create_error', { error: msg });
	onError({ message: msg, type });
}

export function useEndorsementSubmit(params: SubmitParams) {
	const { petitionId, formData, utilityContext, track, onSuccess, onError, setIsSubmitting, uploader, image } = params;

	const submit = useCallback(async () => {
		setIsSubmitting(true);
		try {
			const uploadResult = await getPhotoUrl(image, uploader);
			if (uploadResult && !uploadResult.success) {
				handleSubmitError(track, onError, uploadResult.error, 'SERVER_ERROR');
				return;
			}
			const input = buildEndorsementInput(petitionId, formData, uploadResult?.url);
			const response = await createEndorsement(input, utilityContext);
			if (response.success) {
				void track('endorsement_created', buildTrackingData(formData));
				onSuccess();
			} else {
				const errorMsg = response.errorMessage || 'Failed to create endorsement';
				handleSubmitError(track, onError, errorMsg, getErrorType(errorMsg));
			}
		} catch (e) {
			handleSubmitError(track, onError, e instanceof Error ? e.message : 'Unexpected error', 'NETWORK_ERROR');
		} finally {
			setIsSubmitting(false);
		}
	}, [petitionId, formData, utilityContext, track, onSuccess, onError, setIsSubmitting, uploader, image]);

	return { submit };
}
